import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import moment from 'moment';
import {useQuery} from 'react-query';
import {toast} from 'react-toastify';
import * as Actions from '../../../store/actions';
import EasyLookupModals from '../EasyLookupModals';
import {
  DashboardLayout,
  PageLimit,
  Pagination,
  PrimaryButton,
  TableLayout,
  EmptyState,
  Loader,
  SearchInput,
  SecondaryButton,
} from '../../../components';
import FilterV2 from '../../../components/FilterV2';

import {
  corruptedFile,
  eye,
  failed,
  greenCheckBg,
  lookupEmpty,
} from '../../../assets/images/images';
import {Constants, mapToOptionsArray, errorCodeColor} from '../../../helpers';
import {getCookieValue} from '../../../helpers/getCookie';
import {PAGES_URL} from '../../../helpers/constants';
import {singleIndividualLookupTableHeader} from '../LookupUtils';
import {fetchApiLogsFilters} from '../../../requests/queries/dashboard';
import {OverviewCards} from '../../../components/OverviewCards';
import {shortenLargeNumbers} from '../../../helpers/shortenLargeNumbers';
import {buildTableCardData} from '../../../helpers/buildTableCardData';
import {useLookupHistoryAnalysis} from '../../../hooks/useLookupHistoryAnalysis';
import {useAllApps} from '../../../hooks/useApps';
import {INDIVIDUALS_MODEL_TYPES} from '../../../store/actions';
import Breadcrumbs from 'components/BreadCrumbs';
import PageNumber from '../../../components/PageNumber';
import {useLookupHistory} from '../../../hooks/useLookupHistory';
import {dateRanges} from 'helpers/getDateRange';

function IndividualSingleLookup({
  children,
  getLookupHistory,
  fetchLookup,
  changeLookupPage,
  getApiLogs,
  lookup: {h_loading},
  auth: {userDetails},
  apiLogs: {logs, loading: apiLoading},
}) {
  const [showFilter, setShowFilter] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [openIndividualLookup, setOpenIL] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentFilters, setCurrentFilters] = useState({
    date: 'seven_days',
    dateRange: dateRanges.seven_days(),
    apps: undefined,
    mode: undefined,
    model_type: undefined,
    status: undefined,
    country: undefined,
  });

  const {userApps: allAppsData} = useAllApps();
  const {data: fetchedFilterOptions} = useQuery(
    ['filters'],
    fetchApiLogsFilters,
  );

  const [analysisFilters, setAnalysisFilters] = useState({});
  const {
    analysisData,
    analysisLoading,
    refetch: refetchAnalysis,
  } = useLookupHistoryAnalysis({
    IDENTITY_TYPES: INDIVIDUALS_MODEL_TYPES,
    otherParams: analysisFilters,
  });

  const {
    historyData: history,
    historyLoading,
    refetch: refetchHistory,
  } = useLookupHistory({
    IDENTITY_TYPES: INDIVIDUALS_MODEL_TYPES,
    otherParams: {
      ...analysisFilters,
      limit,
      page: currentPage,
      search: searchTerm,
    },
  });

  const env = getCookieValue('env');

  const filterDefinitions = useMemo(() => {
    const definitions = [];

    if (allAppsData && allAppsData.length > 0) {
      definitions.push({
        key: 'apps',
        label: 'Apps',
        type: 'select',
        options: mapToOptionsArray(allAppsData),
        multiSelect: true,
      });
    }

    if (fetchedFilterOptions?.modes) {
      definitions.push({
        key: 'mode',
        label: 'Mode',
        type: 'select',
        options: mapToOptionsArray(fetchedFilterOptions.modes, '', true),
        multiSelect: true,
      });
    }

    if (Constants.lookups.governmentLookupIDTypes.length > 0) {
      definitions.push({
        key: 'model_type',
        label: 'ID Type',
        type: 'select',
        options: Constants.lookups.governmentLookupIDTypes,
        multiSelect: true,
      });
    }

    // definitions.push({
    //   key: 'status',
    //   label: 'Status',
    //   type: 'select',
    //   options: Constants.lookups.status,
    //   multiSelect: false,
    // });
    //
    // definitions.push({
    //   key: 'country',
    //   label: 'Country',
    //   type: 'select',
    //   options: COUNTRIES,
    //   multiSelect: true,
    // });

    return definitions;
  }, [allAppsData, fetchedFilterOptions]);

  const handleFilterChange = newValuesFromFilterV2 => {
    setCurrentFilters({...newValuesFromFilterV2});
    setCurrentPage(1);
  };

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const hasActiveFilters = useMemo(
    () =>
      Object.entries(currentFilters).some(([key, value]) => {
        if (key === 'dateRange' && !value) return false;
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== '';
      }),
    [currentFilters],
  );

  useEffect(() => {
    if (env === 'Sandbox') {
      Object.values(userDetails?.permissions?.logPermissions || {}).some(
        value => value,
      ) && getApiLogs({page: currentPage, limit});
    } else if (!children) {
      const apiParams = {
        limit,
        page: currentPage,
        company_id: userDetails?.company?.id,
        search: searchTerm || undefined,
      };

      if (currentFilters.apps && currentFilters.apps.length > 0) {
        apiParams.apps = currentFilters.apps.join(',');
      } else if (
        allAppsData &&
        allAppsData.length > 0 &&
        !currentFilters.apps
      ) {
        apiParams.apps = allAppsData.map(app => app.value).join(',');
      }

      if (currentFilters.mode && currentFilters.mode.length > 0) {
        apiParams.mode = currentFilters.mode.join(',');
      }
      if (currentFilters.model_type && currentFilters.model_type.length > 0) {
        apiParams.model_type = currentFilters.model_type;
      }
      if (currentFilters.status) {
        apiParams.status = currentFilters.status;
      }
      if (currentFilters.country && currentFilters.country.length > 0) {
        apiParams.country = currentFilters.country.join(',');
      }
      if (
        currentFilters.dateRange &&
        currentFilters.dateRange.start &&
        currentFilters.dateRange.end
      ) {
        apiParams.start_date = currentFilters.dateRange.start;
        apiParams.end_date = currentFilters.dateRange.end;
      }

      if (userDetails?.company?.id && apiParams.apps) {
        const newAnalysisFilters = {
          app_ids: apiParams.apps,
          entity_types: apiParams.model_type?.join(',') || '',
          api_mode: apiParams.mode,
          status: apiParams.status,
          country: apiParams.country,
          date_from: apiParams.start_date,
          date_to: apiParams.end_date,
        };
        Object.keys(newAnalysisFilters).forEach(
          key =>
            newAnalysisFilters[key] === undefined &&
            delete newAnalysisFilters[key],
        );
        setAnalysisFilters(newAnalysisFilters);
        refetchAnalysis();
        refetchHistory();
      }
    }
  }, [
    allAppsData,
    currentPage,
    limit,
    userDetails?.company?.id,
    userDetails?.permissions?.logPermissions,
    searchTerm,
    currentFilters,
    env,
    getApiLogs,
    children,
    refetchAnalysis,
    refetchHistory,
  ]);

  const SANDBOX_DATA = useMemo(() => {
    if (!apiLoading && logs && logs.api_logs) {
      return logs.api_logs.map((log, index) => ({
        entity_name: 'John Doe',
        entity_type: 'phone_number',
        id_number: `ID-${index}`,
        status_code: '200',
        createdAt: log?.createdAt,
      }));
    }
    return [];
  }, [logs, apiLoading]);

  useEffect(() => {
    if (env === 'Sandbox' && !apiLoading && logs) {
      setData(SANDBOX_DATA);
    } else {
      !historyLoading && history && setData([...(history?.rows || [])]);
    }
  }, [env, history, apiLoading, SANDBOX_DATA, historyLoading, logs]);

  const handleExport = () => {
    const apiParams = {
      limit: history?.count || 10000,
      page: 1,
      company_id: userDetails?.company?.id,
      search: searchTerm || undefined,
      download: true,
    };

    if (currentFilters.apps && currentFilters.apps.length > 0) {
      apiParams.apps = currentFilters.apps.join(',');
    } else if (allAppsData && allAppsData.length > 0) {
      apiParams.apps = allAppsData.map(app => app.value).join(',');
    }
    if (currentFilters.mode && currentFilters.mode.length > 0)
      apiParams.mode = currentFilters.mode.join(',');
    if (currentFilters.model_type && currentFilters.model_type.length > 0)
      apiParams.model_type = currentFilters.model_type.join(',');
    if (currentFilters.status) apiParams.status = currentFilters.status;
    if (currentFilters.country && currentFilters.country.length > 0)
      apiParams.country = currentFilters.country.join(',');
    if (
      currentFilters.dateRange &&
      currentFilters.dateRange.start &&
      currentFilters.dateRange.end
    ) {
      apiParams.start_date = currentFilters.dateRange.start;
      apiParams.end_date = currentFilters.dateRange.end;
    }

    if (userDetails?.company?.id && apiParams.apps) {
      getLookupHistory(apiParams);
      // toast.info("Export started. You'll be notified when it's ready.");
    } else {
      toast.error('Cannot start export without company and app context.');
    }
  };

  const isOwner = userDetails?.permissions?.role === 'Owner';
  const {performSingleIndividualLookup, downloadIndividualLookup} =
    useMemo(() => userDetails?.permissions?.lookupPermissions, [userDetails]) ||
    {};
  const performRight = isOwner ? false : !performSingleIndividualLookup;

  const handleSearchChange = value => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const EmptyStateComponent = () => {
    const isLoading = historyLoading || (env === 'Sandbox' && apiLoading);
    const isDisabled = !userDetails?.company?.verified || performRight;
    const isNoDataInSandbox =
      env === 'Sandbox' && !apiLoading && data?.length === 0;
    const isEmptyArray = !historyLoading && !apiLoading && data?.length === 0;

    if (isLoading) {
      return <Loader height={50} />;
    }

    const commonEmptyStateProps = {
      onClick: () => setOpenIL(!openIndividualLookup),
      src: lookupEmpty,
      disabled: isDisabled,
      style: {cursor: isDisabled ? 'not-allowed' : 'pointer'},
      imageHeight: 48,
      imageWidth: 48,
      height: 55,
    };

    if (hasActiveFilters && isEmptyArray && !searchTerm) {
      return (
        <EmptyState
          body={
            'This filter did not return any data, try using different values.'
          }
          src={corruptedFile}
          noBtn
          customBtn={
            <button
              onClick={() => {
                setShowFilter(true);
                handleFilterChange({});
              }}
              className="text-brandBlue p-4 text-sm font-medium"
            >
              Update preferences
            </button>
          }
        />
      );
    }
    if (isEmptyArray && searchTerm) {
      return (
        <EmptyState
          body={`No results found for "${searchTerm}". Try a different search.`}
          src={corruptedFile}
          noBtn
        />
      );
    }
    if (
      (isEmptyArray || isNoDataInSandbox) &&
      !hasActiveFilters &&
      !searchTerm
    ) {
      return (
        <EmptyState
          body="No historical record available"
          {...commonEmptyStateProps}
        />
      );
    }

    return null;
  };

  const transactionsSummary = [
    {
      icon: greenCheckBg,
      alt: 'successful',
      title: 'Successful',
      value: shortenLargeNumbers(analysisData?.success_count || 0, null, {
        startAt: 'M',
        decimals: 0,
      }),
    },
    {
      icon: failed,
      alt: 'failed',
      title: 'Failed',
      value: shortenLargeNumbers(analysisData?.failed_count || 0, null, {
        startAt: 'M',
        decimals: 0,
      }),
    },
  ];

  const tableCardData = buildTableCardData(
    {count: history?.count, title: 'Total Lookups'},
    transactionsSummary,
  );

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Government Lookup</p>
      }
    >
      <EasyLookupModals
        open={openIndividualLookup}
        setOpen={setOpenIL}
        lookupType="Single"
        batch={false}
      />

      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <Breadcrumbs
          items={[
            {
              label: 'Individual Single Lookup',
              path: PAGES_URL.INDIVIDUAL_LOOKUP,
            },
            {label: 'History'},
          ]}
        />
      </div>

      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Search history by name, ID..."
            onChange={handleSearchChange}
            defaultValue={searchTerm}
          />
          <SecondaryButton
            buttonText="Filter"
            onClick={toggleFilterVisibility}
          />
        </div>

        {downloadIndividualLookup && (
          <PrimaryButton
            buttonText="Export"
            className="w-[100px]"
            onClick={handleExport}
            disabled={!downloadIndividualLookup}
            title={
              !downloadIndividualLookup
                ? 'You do not have permission to perform this action'
                : ''
            }
            loading={h_loading}
          />
        )}
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2
          filters={filterDefinitions}
          onFilterChange={handleFilterChange}
          initialValues={currentFilters}
        />
      </div>

      {children ?? (
        <>
          <OverviewCards
            cardData={tableCardData}
            overviewLoading={analysisLoading}
            className={`grid md:grid-cols-${
              tableCardData?.length > 3
                ? tableCardData?.length - 1
                : tableCardData?.length > 0
                ? tableCardData.length
                : 1
            } xl:grid-cols-${
              tableCardData?.length > 0 ? tableCardData.length : 1
            } gap-2 lg:gap-4 flex-wrap my-5`}
          />

          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                {singleIndividualLookupTableHeader.map(({label, key}) => (
                  <th
                    key={key}
                    className={`${
                      label === 'Name'
                        ? 'pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap'
                        : 'pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap'
                    }`}
                  >
                    <div className="flex items-end gap-1 cursor-pointer">
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>

            <tbody className="text-sm bg-white">
              {(historyLoading || (env === 'Sandbox' && apiLoading)) && (
                <tr>
                  <td colSpan={singleIndividualLookupTableHeader.length + 1}>
                    <Loader height={50} />
                  </td>
                </tr>
              )}
              {!(historyLoading || (env === 'Sandbox' && apiLoading)) &&
                data?.length === 0 && (
                  <tr>
                    <td colSpan={singleIndividualLookupTableHeader.length + 1}>
                      <EmptyStateComponent />
                    </td>
                  </tr>
                )}
              {!(historyLoading || (env === 'Sandbox' && apiLoading)) &&
                data &&
                data?.map((record, i) => (
                  <tr
                    onClick={() => {
                      if (record.status_code !== '200') return;
                      setOpenIL(!openIndividualLookup);
                      changeLookupPage(2);
                      localStorage.setItem(
                        'callerId',
                        record?.caller_name || record?.api_mode,
                      );
                      env !== 'Sandbox' &&
                        fetchLookup({
                          id: record?.entity_id || record?.model_type_id,
                          type:
                            record?.entity_type?.toLowerCase() ||
                            record?.model_type?.toLowerCase(),
                          history_id: record?.history_id,
                        });
                    }}
                    className="border-b cursor-pointer border-grey60 hover:bg-gray-50"
                    key={record?.history_id || record?.model_type_id || i}
                  >
                    <td className="p-4 pl-4 sm:pl-6 xl:pl-12 text-sm text-body whitespace-nowrap">
                      {`${record?.entity_name || '-'}`}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {record?.entity_type?.replaceAll('_', ' ') ||
                        record?.model_type?.replaceAll('_', ' ') ||
                        '-'}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {record?.id_number?.toUpperCase() || '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <div
                        className={`px-2 pt-[5px] pb-[3px] text-xs font-medium uppercase rounded-full w-fit ${
                          // ['completed', '200']?.includes(
                          //   record?.status_code,
                          // )
                          //   ? 'text-success20 bg-success'
                          //   : ['failed', '400', '500']?.includes(
                          //       record?.status_code,
                          //     )
                          //   ? 'text-white bg-status-404'
                          //   : 'text-white bg-brandBlue'
                          errorCodeColor(record?.status_code)?.bg
                        } ${errorCodeColor(record?.status_code)?.color}`}
                      >
                        {record?.status_code === '404'
                          ? 'User Not Found'
                          : record?.status_code === '200'
                          ? 'User Found'
                          : 'Failed'}
                      </div>
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {moment(record?.createdAt).format(
                        'MMM DD, YYYY hh:mmA',
                      ) || '-'}
                    </td>
                    {record?.status_code === '200' && (
                      <td className="p-4 pl-0 whitespace-nowrap">
                        <div className="flex items-center gap-4">
                          <button title="View" className="h-4 aspect-square">
                            <img src={eye} alt="View" />
                          </button>
                        </div>
                      </td>
                    )}
                    {record?.status_code !== '200' && (
                      <td className="p-4 pl-0 whitespace-nowrap"></td>
                    )}
                  </tr>
                ))}
            </tbody>
          </TableLayout>

          {!(historyLoading || (env === 'Sandbox' && apiLoading)) &&
            data?.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 -mx-8 px-8 border-t border-[#E4E3EC] py-[18px]">
                <PageNumber
                  currentPage={+currentPage}
                  limit={limit}
                  totalCount={history?.count}
                />
                <div className="flex items-center gap-5">
                  <PageLimit
                    onLimitChange={l => {
                      setLimit(l);
                      setCurrentPage(1);
                    }}
                    total={history && history?.count}
                    length={history?.rows?.length}
                    limit={limit}
                  />
                  <Pagination
                    total={history && Math.ceil(history?.count / limit)}
                    current={+currentPage}
                    onPageChange={activePage => {
                      pageClick(activePage);
                    }}
                  />
                </div>
              </div>
            )}
        </>
      )}
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(IndividualSingleLookup);
